import type { ProjectEvent } from "@/engine/types";

export const act4Events: ProjectEvent[] = [
  {
    id: "uat_signoff",
    title: "UAT Signoff, With Caveats",
    sender: "Business Owner",
    day: "Day 12+",
    eligibleWhen: (state) => state.day >= 12 && !state.resolvedEventIds.includes("uat_signoff"),
    body: [
      "Clinicians are ready to sign off UAT — but two minor defects are still open, both cosmetic on the surface.",
      "Business owner: \"We can sign off now and track these as known issues, or hold a couple more days to close them first.\"",
    ],
    choices: [
      {
        id: "fix_before_signoff",
        text: "Hold UAT until the defects are closed",
        description: "A couple more days now, cleaner signoff.",
        effects: [
          { metric: "schedule", delta: -4 },
          { metric: "quality", delta: 8 },
        ],
        resultText: "UAT signs off clean, no open items carried into go-live.",
      },
      {
        id: "signoff_with_known_issues",
        text: "Sign off now, track defects as known issues",
        description: "Keep the date moving, fix in parallel.",
        effects: [
          { metric: "schedule", delta: 4 },
          { metric: "quality", delta: -8 },
          { metric: "stakeholderTrust", delta: -3 },
        ],
        resultText: "UAT signs off with two open items — technically clean, quietly not.",
      },
    ],
  },
  {
    id: "go_live_gate",
    title: "The Go-Live Decision",
    sender: "Director",
    day: "Day 13+",
    eligibleWhen: (state) => state.resolvedEventIds.includes("uat_signoff") && state.day >= 13,
    body: [
      "You're close to your committed date. The pipeline is validated, UAT signed off — but the security team hasn't sent formal written sign-off. They said \"looks fine\" verbally, but nothing's landed in writing.",
      "Director: \"Are we good for go-live?\"",
    ],
    choices: [
      {
        id: "hold_for_written_signoff",
        text: "Hold go-live until written security sign-off lands",
        description: "A verbal 'looks fine' isn't a closed control.",
        effects: [
          { metric: "schedule", delta: -3 },
          { metric: "quality", delta: 8 },
          { metric: "stakeholderTrust", delta: 5 },
          { metric: "operationalRisk", delta: -10 },
        ],
        resultText:
          "Go-live slips while paperwork catches up. Leadership notes you held the line on a control instead of a slide.",
      },
      {
        id: "go_live_on_verbal",
        text: "Go live on the verbal confirmation",
        description: "Chase the written sign-off in parallel.",
        effects: [
          { metric: "schedule", delta: 5 },
          { metric: "quality", delta: -8 },
          { metric: "stakeholderTrust", delta: -5 },
          { metric: "operationalRisk", delta: 15 },
        ],
        resultText:
          "It works out this time — the written sign-off arrives after the fact with no changes. But you shipped a PHI system on an unlogged verbal approval, and that's the kind of gap an audit finds.",
      },
    ],
  },
  {
    id: "hospital_one_retro",
    title: "The Retro",
    sender: "Director",
    day: "Go-Live +",
    eligibleWhen: (state) => state.resolvedEventIds.includes("go_live_gate"),
    body: [
      "The dashboard is live. Your director asks you to run a short retro with the team before moving to hospital #2.",
      "How you close this out shapes whether the team wants to work with you on the next nine hospitals.",
    ],
    choices: [
      {
        id: "blameless_retro",
        text: "Run a blame-free retro, capture what to reuse",
        description: "Focus on the estimation gap and what template to carry forward.",
        effects: [
          { metric: "teamTrust", delta: 12 },
          { metric: "stakeholderTrust", delta: 5 },
          { metric: "quality", delta: 5 },
        ],
        resultText:
          "The team leaves with a reusable estimation template. Hospital #2 starts from a stronger baseline than hospital #1 did.",
      },
      {
        id: "skip_retro_move_on",
        text: "Skip the retro, move straight to hospital #2",
        description: "No time to lose — nine more hospitals to go.",
        effects: [
          { metric: "teamTrust", delta: -10 },
          { metric: "quality", delta: -5 },
        ],
        resultText:
          "You move fast, but you carry the same blind spots into hospital #2 — nothing was actually learned, only repeated.",
      },
    ],
  },
];
